import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Alert, Card, Modal, Space, Spin, Typography } from 'antd';
import V4Tabs from './V4Tabs';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

const DocumentsImportV4 = () => {
  const { getAuthHeaders } = useAuth();
  const headersAuth = useMemo(() => ({ ...getAuthHeaders() }), [getAuthHeaders]);
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('product'); // product | document_link | cpn | alt_part_number | supplier
  const [serverFields, setServerFields] = useState([]);
  const [display, setDisplay] = useState({});
  const [required, setRequired] = useState([]);
  const [parsed, setParsed] = useState({ headers: [], sample: [], total_rows: 0 });
  const [mapping, setMapping] = useState({});
  const [headerRow, setHeaderRow] = useState(1);
  const [attributeValueMode, setAttributeValueMode] = useState('none'); // none | header_only | pattern_only | hybrid
  const [attributeHeaderColumns, setAttributeHeaderColumns] = useState([]);
  const [parsingFile, setParsingFile] = useState(false);
  const [startingJob, setStartingJob] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parseNotice, setParseNotice] = useState('');
  const [presetNotice, setPresetNotice] = useState('');
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [confirmPresetModal, setConfirmPresetModal] = useState({ open: false, preset: null });
  const [confirmOverwriteModal, setConfirmOverwriteModal] = useState({ open: false, name: '', presetId: null });
  const [mappingFilter, setMappingFilter] = useState('all'); // all | missing | product | document

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/import/headers`, { headers: headersAuth });
      const data = await res.json();
      setServerFields(data.fields || []);
      setDisplay(data.display || {});
      setRequired(data.required || []);
    })();
  }, [headersAuth]);

  const loadPresets = useCallback(async () => {
    try {
      setPresetsLoading(true);
      const params = new URLSearchParams({ mode: importMode });
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/mappings?${params.toString()}`, { headers: headersAuth });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Failed to load mapping presets');
      }
      setPresets(data.presets || []);
    } catch (err) {
      setPresetNotice(err?.message || 'Failed to load mapping presets');
    } finally {
      setPresetsLoading(false);
    }
  }, [headersAuth, importMode]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const presetStorageKey = useMemo(() => `v4Import:lastPreset:${importMode}`, [importMode]);

  const autoMapFields = useCallback(
    (baseMapping = {}, overrideExisting = false) => {
      if (!parsed.headers.length || !serverFields.length) {
        return { mapping: baseMapping, changed: false };
      }
      const next = overrideExisting ? {} : { ...baseMapping };
      let changed = false;
      const lowerHeaders = (parsed.headers || []).map((h) => h.toLowerCase());
      serverFields.forEach((field) => {
        const key = field.field;
        if (!overrideExisting && next[key]) {
          return;
        }
        const candidates = [
          (display[key] || '').toLowerCase(),
          (key || '').toLowerCase(),
        ].filter(Boolean);
        const matchIndex = lowerHeaders.findIndex((header) =>
          candidates.includes(header)
        );
        if (matchIndex >= 0) {
          const header = parsed.headers[matchIndex];
          next[key] = header;
          changed = true;
        }
      });
      return { mapping: next, changed };
    },
    [parsed.headers, serverFields, display]
  );

  useEffect(() => {
    setMapping((prev) => {
      const { mapping: next, changed } = autoMapFields(prev, false);
      return changed ? next : prev;
    });
  }, [autoMapFields]);

  const parseSelectedFile = useCallback(async (selectedFile, selectedHeaderRow = headerRow, showSuccessNotice = false) => {
    if (!selectedFile) {
      setParsed({ headers: [], sample: [], total_rows: 0 });
      setMapping({});
      setParseNotice('');
      setParsingFile(false);
      return;
    }
    setParsingFile(true);
    setParseError('');
    setParseNotice('');
    const fd = new FormData();
    fd.append('file', selectedFile);
    fd.append('header_row', String(Math.max(1, Number(selectedHeaderRow) || 1)));
    const h = { ...headersAuth };
    try { delete h['Content-Type']; delete h['content-type']; } catch {}
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/import/parse`, { method: 'POST', headers: h, body: fd });
      if (!res.ok) {
        let txt = '';
        try { const j = await res.json(); txt = j?.detail || JSON.stringify(j); } catch { txt = await res.text(); }
        setParseError(txt || 'Parse failed');
        setParseNotice('');
        return;
      }
      const data = await res.json();
      setParsed({ headers: data.headers || [], sample: data.sample || [], total_rows: data.total_rows || 0 });
      setParseError('');
      setMapping({});
      if (showSuccessNotice) {
        const cols = (data.headers || []).length;
        setParseNotice(`Header row ${Math.max(1, Number(selectedHeaderRow) || 1)} re-parsed successfully (${cols} column${cols === 1 ? '' : 's'} detected).`);
      }
    } catch (err) {
      setParseError(err?.message || 'Failed to read file');
      setParseNotice('');
    } finally {
      setParsingFile(false);
    }
  }, [headerRow, headersAuth]);

  const onChoose = async (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setParsed({ headers: [], sample: [], total_rows: 0 });
    setMapping({});
    if (!f) return;
    await parseSelectedFile(f, headerRow, false);
  };

  const handleMappingChange = (target, value) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[target];
      } else {
        next[target] = value;
      }
      return next;
    });
  };

  const handleAutoMapClick = (overrideExisting = false) => {
    setMapping((prev) => {
      const base = overrideExisting ? {} : prev;
      const { mapping: next } = autoMapFields(base, overrideExisting);
      return next;
    });
  };

  const headerSamples = useMemo(() => {
    const sampleRows = parsed.sample || [];
    if (!sampleRows.length) return {};
    const map = {};
    (parsed.headers || []).forEach((header) => {
      const values = sampleRows
        .map((row) => row[header])
        .filter((val) => val !== undefined && val !== null && String(val).trim() !== '');
      map[header] = values.slice(0, 3);
    });
    return map;
  }, [parsed.sample, parsed.headers]);

  // "Only" sets restrict which serverFields are selectable in modes with a
  // fixed, small field list. Modes not listed here (currently only "product")
  // show every serverField except the other modes' exclusive fields below.
  const MODE_ONLY_FIELDS = useMemo(() => ({
    document_link: new Set(['product.product_code', 'document.original_filename', 'document.job_id']),
    cpn: new Set(['product.product_code', 'product.customer_id', 'product.customer_part_number']),
    alt_part_number: new Set(['product.product_code', 'alt_part_number.value', 'product.name']),
    supplier: new Set(['product.product_code', 'supplier.supplier_name', 'supplier.supplier_part_number', 'product.name']),
  }), []);

  // Fields exclusive to a single mode's "only" set (other than product.product_code,
  // which every mode shares) must never be shown/mappable outside that mode.
  const modeExclusiveFieldSet = useMemo(() => {
    const set = new Set();
    Object.entries(MODE_ONLY_FIELDS).forEach(([mode, fields]) => {
      fields.forEach((field) => {
        // product.product_code and product.name are shared, always-visible
        // fields even when listed in a mode's "only" set -- never treat them
        // as exclusive to that one mode.
        if (field !== 'product.product_code' && field !== 'product.name') set.add(field);
      });
    });
    return set;
  }, [MODE_ONLY_FIELDS]);

  const activeOnlyFields = MODE_ONLY_FIELDS[importMode];

  const isFieldVisibleForMode = useCallback(
    (fieldKey) => (activeOnlyFields ? activeOnlyFields.has(fieldKey) : !modeExclusiveFieldSet.has(fieldKey)),
    [activeOnlyFields, modeExclusiveFieldSet]
  );

  const effectiveRequired = useMemo(() => {
    if (importMode === 'document_link') {
      return ['product.product_code', 'document.original_filename'];
    }
    if (importMode === 'cpn') {
      return ['product.product_code', 'product.customer_id', 'product.customer_part_number'];
    }
    if (importMode === 'alt_part_number') {
      return ['product.product_code', 'alt_part_number.value'];
    }
    if (importMode === 'supplier') {
      return ['product.product_code', 'supplier.supplier_name', 'supplier.supplier_part_number'];
    }
    return required;
  }, [importMode, required]);

  const requiredMissing = useMemo(
    () => effectiveRequired.filter((field) => !mapping[field]),
    [effectiveRequired, mapping]
  );

  const mappingSummary = useMemo(() => {
    const mappedCount = Object.keys(mapping || {}).length;
    const totalFields = activeOnlyFields
      ? activeOnlyFields.size
      : serverFields.filter((field) => isFieldVisibleForMode(field.field)).length;
    return {
      mappedCount,
      totalFields,
      remainingRequired: requiredMissing.length,
    };
  }, [mapping, serverFields, requiredMissing.length, activeOnlyFields, isFieldVisibleForMode]);

  useEffect(() => {
    setMapping((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([key, value]) => {
        if (isFieldVisibleForMode(key)) {
          next[key] = value;
        }
      });
      return next;
    });
  }, [importMode, isFieldVisibleForMode]);

  useEffect(() => {
    if (!(parsed.headers || []).length) {
      setAttributeHeaderColumns([]);
      return;
    }
    setAttributeHeaderColumns((prev) => prev.filter((h) => (parsed.headers || []).includes(h)));
  }, [parsed.headers]);

  const filteredFields = useMemo(() => {
    return serverFields.filter((field) => {
      if (!isFieldVisibleForMode(field.field)) {
        return false;
      }
      if (mappingFilter === 'missing') {
        return !mapping[field.field];
      }
      // Product/Document quick filters only make sense in "product" mode,
      // where both prefixes coexist in a large field list. Other modes have
      // a small fixed field set already scoped by isFieldVisibleForMode above.
      if (importMode === 'product' && mappingFilter === 'product') {
        return field.field.startsWith('product.');
      }
      if (importMode === 'product' && mappingFilter === 'document') {
        return field.field.startsWith('document.');
      }
      return true;
    });
  }, [serverFields, mappingFilter, mapping, importMode, isFieldVisibleForMode]);

  const samplePreviewForField = (fieldKey) => {
    const header = mapping[fieldKey];
    if (!header) return null;
    const values = headerSamples[header];
    if (!values || values.length === 0) return null;
    return values.join(', ');
  };

  const buildMappingPayload = useCallback(() => {
    const headerModeEnabled = attributeValueMode === 'header_only' || attributeValueMode === 'hybrid';
    const patternModeEnabled = attributeValueMode === 'pattern_only' || attributeValueMode === 'hybrid';
    const sourceOrder = [];
    if (headerModeEnabled) sourceOrder.push('header');
    if (patternModeEnabled) sourceOrder.push('pattern');
    const fieldMappings = { ...(mapping || {}) };
    return {
      ...fieldMappings,
      __field_mappings: fieldMappings,
      __attribute_config: {
        enabled: attributeValueMode !== 'none',
        source_order: sourceOrder,
        header_sources: attributeHeaderColumns.map((column) => ({ column })),
        pattern_sources: {
          name_prefix: 'ATTR_NAME_',
          value_prefix: 'ATTR_VALUE_',
          uom_prefix: 'ATTR_UOM_',
        },
        rules: {
          cross_source_conflict: 'keep_first_seen_non_empty',
          intra_row_duplicate: 'concat_unique_pipe',
          concat_value_uom_with_space: true,
        },
      },
    };
  }, [mapping, attributeValueMode, attributeHeaderColumns]);

  const applyMappingPayload = useCallback((payload) => {
    const cfg = payload?.__attribute_config || {};
    const sourceOrder = Array.isArray(cfg?.source_order) ? cfg.source_order : [];
    const hasHeader = sourceOrder.includes('header');
    const hasPattern = sourceOrder.includes('pattern');
    let mode = 'none';
    if (hasHeader && hasPattern) mode = 'hybrid';
    else if (hasHeader) mode = 'header_only';
    else if (hasPattern) mode = 'pattern_only';
    setAttributeValueMode(mode);
    const hdrs = Array.isArray(cfg?.header_sources)
      ? cfg.header_sources.map((x) => (x?.column || '')).filter(Boolean)
      : [];
    setAttributeHeaderColumns(hdrs);

    const nestedFieldMappings = (payload && typeof payload === 'object' && payload.__field_mappings && typeof payload.__field_mappings === 'object')
      ? payload.__field_mappings
      : null;
    const base = nestedFieldMappings ? { ...nestedFieldMappings } : { ...(payload || {}) };
    delete base.__attribute_config;
    delete base.__field_mappings;
    delete base.__attribute_value_mode;
    delete base.__attribute_value_column;
    setMapping(base);
  }, []);

  const startImportJob = async () => {
    if (!file) {
      alert('Please select a CSV/XLSX file first.');
      return;
    }
    if (requiredMissing.length) {
      alert(`Map all required fields before importing: ${requiredMissing.map((field) => display[field] || field).join(', ')}`);
      return;
    }
    const headerModeEnabled = attributeValueMode === 'header_only' || attributeValueMode === 'hybrid';
    if (headerModeEnabled && attributeHeaderColumns.length === 0) {
      alert('Select one or more source columns for header-based attribute mapping.');
      return;
    }
    setStartingJob(true);
    try {
      const mappingPayload = buildMappingPayload();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping_json', JSON.stringify(mappingPayload));
      fd.append('mode', importMode);
      fd.append('header_row', String(Math.max(1, Number(headerRow) || 1)));
      const h = { ...headersAuth };
      try { delete h['Content-Type']; delete h['content-type']; } catch {}
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/import/jobs`, {
        method: 'POST',
        headers: h,
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || 'Import job failed to start');
      }
      alert('Import job queued. You can track status in the Import Jobs tab.');
    } catch (err) {
      alert(err.message || 'Failed to start import job');
    } finally {
      setStartingJob(false);
    }
  };

  const persistPreset = useCallback(async (name) => {
    try {
      setSavingPreset(true);
      setPresetNotice('');
      const resp = await fetch(`${API_BASE_URL}/api/v4/documents/import/mappings`, {
        method: 'POST',
        headers: {
          ...headersAuth,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          mode: importMode,
          mapping: buildMappingPayload(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.detail || 'Failed to save mapping preset');
      }
      setPresetNotice(`Saved preset "${name}".`);
      setPresetName('');
      await loadPresets();
      return true;
    } catch (err) {
      setPresetNotice(err?.message || 'Failed to save mapping preset');
      return false;
    } finally {
      setSavingPreset(false);
    }
  }, [headersAuth, importMode, buildMappingPayload, loadPresets]);

  const saveCurrentPreset = async () => {
    const name = String(presetName || '').trim();
    if (!name) {
      alert('Enter a preset name first.');
      return;
    }
    const existing = (presets || []).find((p) => String(p.preset_name || '').trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      setConfirmOverwriteModal({ open: true, name, presetId: existing.id });
      return;
    }
    await persistPreset(name);
  };

  const applySelectedPreset = useCallback(() => {
    const preset = (presets || []).find((p) => String(p.id) === String(selectedPresetId));
    if (!preset) {
      alert('Select a preset first.');
      return;
    }
    applyMappingPayload(preset.mapping_config || {});
    setPresetNotice(`Applied preset "${preset.preset_name}".`);
    localStorage.setItem(presetStorageKey, String(preset.id));
  }, [presets, selectedPresetId, applyMappingPayload, presetStorageKey]);

  const closeConfirmPresetModal = useCallback(() => {
    setConfirmPresetModal({ open: false, preset: null });
  }, []);

  const confirmApplyDetectedPreset = useCallback(() => {
    const preset = confirmPresetModal?.preset;
    if (!preset) {
      closeConfirmPresetModal();
      return;
    }
    applyMappingPayload(preset.mapping_config || {});
    setPresetNotice(`Applied detected preset "${preset.preset_name}".`);
    localStorage.setItem(presetStorageKey, String(preset.id));
    closeConfirmPresetModal();
  }, [confirmPresetModal, applyMappingPayload, closeConfirmPresetModal, presetStorageKey]);

  const closeConfirmOverwriteModal = useCallback(() => {
    setConfirmOverwriteModal({ open: false, name: '', presetId: null });
  }, []);

  const confirmOverwritePreset = useCallback(async () => {
    const name = String(confirmOverwriteModal?.name || '').trim();
    if (!name) {
      closeConfirmOverwriteModal();
      return;
    }
    await persistPreset(name);
    closeConfirmOverwriteModal();
  }, [confirmOverwriteModal, persistPreset, closeConfirmOverwriteModal]);

  const detectedPatternPairs = useMemo(() => {
    const headersByLower = {};
    (parsed.headers || []).forEach((h) => {
      headersByLower[(h || '').toLowerCase()] = h;
    });
    let count = 0;
    (parsed.headers || []).forEach((h) => {
      const m = (h || '').match(/^ATTR_NAME_(\d+)$/i);
      if (!m) return;
      const idx = m[1];
      if (headersByLower[`attr_value_${idx}`]) {
        count += 1;
      }
    });
    return count;
  }, [parsed.headers]);

  const findMatchingPreset = useCallback((headers) => {
    const headerSet = new Set((headers || []).map((h) => String(h || '').trim().toLowerCase()));
    const hasPatternPair = (() => {
      const lower = {};
      (headers || []).forEach((h) => { lower[String(h || '').trim().toLowerCase()] = true; });
      return (headers || []).some((h) => {
        const m = String(h || '').trim().match(/^ATTR_NAME_(\d+)$/i);
        if (!m) return false;
        return Boolean(lower[`attr_value_${m[1]}`.toLowerCase()]);
      });
    })();

    let best = null;
    let bestScore = -1;
    let bestRequired = 0;
    (presets || []).forEach((preset) => {
      const mappingCfg = preset?.mapping_config || {};
      const fieldMappings = (mappingCfg?.__field_mappings && typeof mappingCfg.__field_mappings === 'object')
        ? mappingCfg.__field_mappings
        : mappingCfg;
      let required = 0;
      let matched = 0;
      Object.entries(fieldMappings).forEach(([key, val]) => {
        if (!key || key.startsWith('__')) return;
        const src = String(val || '').trim().toLowerCase();
        if (!src) return;
        required += 1;
        if (headerSet.has(src)) matched += 1;
      });
      const sourceOrder = mappingCfg?.__attribute_config?.source_order || [];
      if (Array.isArray(sourceOrder) && sourceOrder.includes('pattern')) {
        required += 1;
        if (hasPatternPair) matched += 1;
      }
      if (required === 0) return;
      if (matched !== required) return;
      if (matched > bestScore) {
        best = preset;
        bestScore = matched;
        bestRequired = required;
      }
    });
    if (!best) return null;
    return {
      preset: best,
      matched: bestScore,
      required: bestRequired,
    };
  }, [presets]);

  useEffect(() => {
    if (!parsed.headers.length || !presets.length) return;
    const best = findMatchingPreset(parsed.headers);
    if (!best) return;
    setConfirmPresetModal({ open: true, preset: best.preset, matched: best.matched, required: best.required });
  }, [parsed.headers, presets, findMatchingPreset]);

  useEffect(() => {
    if (!presets.length) return;
    const savedPresetId = localStorage.getItem(presetStorageKey);
    if (!savedPresetId) return;
    const exists = presets.some((p) => String(p.id) === String(savedPresetId));
    if (exists) {
      setSelectedPresetId(String(savedPresetId));
    }
  }, [presets, presetStorageKey]);

  return (
    <div style={{ width: '100%', padding: '16px 20px' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>Documents v4</Typography.Title>
          <Typography.Text type="secondary">
            Import, map, and monitor product-document relationships with reusable mapping presets.
          </Typography.Text>
        </div>
        <V4Tabs />

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <div className="card-body">
          <h6 className="mb-3">Import Product ↔ Document Relations (CSV/XLSX)</h6>
          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <label className="form-label d-block">Import Type</label>
              <div className="btn-group w-100 flex-wrap" role="group" aria-label="Import mode">
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'product' ? 'active' : ''}`}
                  onClick={() => setImportMode('product')}
                  disabled={parsingFile || startingJob}
                >
                  Product + Document Fields
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'document_link' ? 'active' : ''}`}
                  onClick={() => setImportMode('document_link')}
                  disabled={parsingFile || startingJob}
                >
                  Document Link Only
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'cpn' ? 'active' : ''}`}
                  onClick={() => setImportMode('cpn')}
                  disabled={parsingFile || startingJob}
                >
                  CPN Import
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'alt_part_number' ? 'active' : ''}`}
                  onClick={() => setImportMode('alt_part_number')}
                  disabled={parsingFile || startingJob}
                >
                  Alt Part Numbers
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary ${importMode === 'supplier' ? 'active' : ''}`}
                  onClick={() => setImportMode('supplier')}
                  disabled={parsingFile || startingJob}
                >
                  Suppliers / Supplier Part Numbers
                </button>
              </div>
              <div className="form-text mt-2">
                {importMode === 'document_link'
                  ? 'Use this mode to upload a lightweight CSV/XLSX that links existing documents (already uploaded/parsed) to products by Product Code + Document Name or Document Job ID.'
                  : importMode === 'cpn'
                  ? 'Use this mode to map Product Code, Customer ID, and Customer Part Number. Import creates attributes as cpn_<customer_id> = customer_part_number.'
                  : importMode === 'alt_part_number'
                  ? 'Use this mode to attach alternate part numbers to products. Long format: one row per (Product Code, Alt Part Number) pair. Re-importing the same pair is a no-op. A row whose Product Code doesn\'t match an existing product creates a new product (optionally named via Product Name → product.name; otherwise the product code is used as the name).'
                  : importMode === 'supplier'
                  ? 'Use this mode to attach supplier + supplier part number pairs to products. Long format: one row per (Product Code, Supplier Name, Supplier Part Number) triple. Re-importing the same triple is a no-op. A row whose Product Code doesn\'t match an existing product creates a new product (optionally named via Product Name → product.name; otherwise the product code is used as the name).'
                  : 'Use this mode to import or update product and document metadata fields from a complete export file.'}
              </div>
            </div>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Choose CSV/XLSX</label>
              <input
                type="file"
                className="form-control"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={onChoose}
                disabled={parsingFile || startingJob}
              />
              <div className="row g-2 mt-2">
                <div className="col-sm-6">
                  <label className="form-label mb-1">Column header row</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control form-control-sm"
                    value={headerRow}
                    onChange={(e) => setHeaderRow(Math.max(1, Number(e.target.value) || 1))}
                  />
                </div>
                <div className="col-sm-6 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => parseSelectedFile(file, headerRow, true)}
                    disabled={!file || parsingFile || startingJob}
                  >
                    {parsingFile ? 'Reading file…' : 'Re-parse Header Row'}
                  </button>
                </div>
              </div>
              <div className="form-text">
                {importMode === 'document_link'
                  ? 'Required: Product Code → product.product_code and Document Name → document.original_filename (or include document.job_id).'
                  : importMode === 'cpn'
                  ? 'Required: Product Code → product.product_code, Customer ID → product.customer_id, Customer Part Number → product.customer_part_number.'
                  : importMode === 'alt_part_number'
                  ? 'Required: Product Code → product.product_code, Alt Part Number → alt_part_number.value. Optional: Product Name → product.name (used when a new product is created).'
                  : importMode === 'supplier'
                  ? 'Required: Product Code → product.product_code, Supplier Name → supplier.supplier_name, Supplier Part Number → supplier.supplier_part_number. Optional: Product Name → product.name (used when a new product is created).'
                  : 'Required: map Product Code/Part Number → product.product_code.'}
              </div>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="alert alert-light border w-100 mb-0">
                {importMode === 'document_link'
                  ? 'Link mode does not create new documents; it only connects existing parsed documents to matching products.'
                  : importMode === 'cpn'
                  ? 'CPN mode updates product attributes only. Each row writes cpn_<customer_id> based on matching product code.'
                  : importMode === 'alt_part_number'
                  ? 'Alt part number mode adds alternate part numbers to products. A row with an unknown product code creates a new product for it (named via the optional Product Name mapping, or the product code itself if unmapped) rather than erroring.'
                  : importMode === 'supplier'
                  ? 'Supplier mode adds supplier/supplier-part-number pairs to products. A row with an unknown product code creates a new product for it (named via the optional Product Name mapping, or the product code itself if unmapped) rather than erroring.'
                  : 'Upload the file to preview headers, then use the dropdowns below to map each field. When you start the import it runs as a background job and the status appears in the table at the bottom.'}
              </div>
            </div>
          </div>

          {importMode === 'product' && (
            <div className="row g-3 mt-1">
              <div className="col-12">
                <div className="border rounded p-3">
                  <div className="fw-semibold mb-2">Attribute Value Mapping Options</div>
                  <div className="row g-2">
                    <div className="col-lg-5">
                      <label className="form-label">Attribute mapping mode</label>
                      <select
                        className="form-select"
                        value={attributeValueMode}
                        onChange={(e) => setAttributeValueMode(e.target.value)}
                      >
                        <option value="none">Manual mapping only (attribute.* fields)</option>
                        <option value="header_only">Header-based columns only</option>
                        <option value="pattern_only">ATTR_NAME_n / ATTR_VALUE_n / ATTR_UOM_n only</option>
                        <option value="hybrid">Header-based + ATTR_NAME_n pattern</option>
                      </select>
                    </div>
                    {(attributeValueMode === 'header_only' || attributeValueMode === 'hybrid') && (
                      <div className="col-lg-7">
                        <label className="form-label">Select source columns</label>
                        <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                          {(parsed.headers || []).length === 0 && (
                            <div className="small text-muted">No parsed headers available yet.</div>
                          )}
                          {(parsed.headers || []).map((header, idx) => {
                            const checked = attributeHeaderColumns.includes(header);
                            return (
                              <div className="form-check" key={`${idx}-${header}`}>
                                <input
                                  id={`attr-header-col-${idx}`}
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setAttributeHeaderColumns((prev) => {
                                      if (isChecked) {
                                        if (prev.includes(header)) return prev;
                                        return [...prev, header];
                                      }
                                      return prev.filter((h) => h !== header);
                                    });
                                  }}
                                />
                                <label className="form-check-label small" htmlFor={`attr-header-col-${idx}`}>
                                  {header}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                        <div className="form-text">
                          Each selected column header becomes an attribute name, and row value becomes attribute value.
                        </div>
                        <div className="small mt-2">
                          <span className="fw-semibold">Selected fields:</span>{' '}
                          {attributeHeaderColumns.length
                            ? attributeHeaderColumns.join(', ')
                            : <span className="text-muted">None</span>}
                        </div>
                      </div>
                    )}
                    {(attributeValueMode === 'pattern_only' || attributeValueMode === 'hybrid') && (
                      <div className="col-12">
                        <div className={`alert ${detectedPatternPairs > 0 ? 'alert-info' : 'alert-warning'} py-2 mb-0`}>
                          {detectedPatternPairs > 0
                            ? `Detected ${detectedPatternPairs} ATTR_NAME_n/ATTR_VALUE_n pair column set(s). ATTR_UOM_n is concatenated to value with a space when present.`
                            : 'No ATTR_NAME_n/ATTR_VALUE_n column pairs detected. Import will continue without pattern attributes.'}
                        </div>
                      </div>
                    )}
                    {attributeValueMode !== 'none' && (
                      <div className="col-12">
                        <div className="small text-muted">
                          Conflict rule: first non-empty value wins across sources. Duplicate ATTR_NAME values in a row append unique values with <code>|</code>.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {parsingFile && (
            <div className="mt-3">
              <Alert
                type="info"
                showIcon
                icon={<Spin size="small" />}
                message="Reading your file"
                description="Detecting column headers, sample rows, and preparing field mapping. This may take a moment for large spreadsheets."
              />
            </div>
          )}
          {parseError && (
            <div className="mt-3">
              <Alert type="error" showIcon message={parseError} />
            </div>
          )}
          {parseNotice && (
            <div className="mt-3">
              <Alert type="success" showIcon message={parseNotice} />
            </div>
          )}

          {startingJob && (
            <div className="mt-3">
              <Alert
                type="info"
                showIcon
                icon={<Spin size="small" />}
                message="Setting up import job"
                description="Uploading your file and creating a background import job. You will be notified when it is queued."
              />
            </div>
          )}

          {parsed.headers.length > 0 && !parsingFile && (
            <div className="mt-4">
              <div className="border rounded p-3 mb-3">
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                  <div className="flex-grow-1">
                    <div className="fw-semibold">Saved Mapping Presets</div>
                    <div className="small text-muted">Save current mapping with a name, or apply an existing preset.</div>
                  </div>
                  <div className="d-flex gap-2 align-items-start">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={loadPresets}
                      disabled={presetsLoading}
                    >
                      {presetsLoading ? 'Loading…' : 'Refresh Presets'}
                    </button>
                  </div>
                </div>
                <div className="row g-2 mt-1">
                  <div className="col-lg-6">
                    <label className="form-label mb-1">Apply preset</label>
                    <div className="input-group">
                      <select
                        className="form-select"
                        value={selectedPresetId}
                        onChange={(e) => setSelectedPresetId(e.target.value)}
                      >
                        <option value="">-- Select preset --</option>
                        {(presets || []).map((preset) => (
                          <option key={preset.id} value={preset.id}>
                            {preset.preset_name}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-outline-primary" onClick={applySelectedPreset}>
                        Apply
                      </button>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label mb-1">Save current mapping as</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Document Export - Vendor A"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveCurrentPreset}
                        disabled={savingPreset}
                      >
                        {savingPreset ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
                {presetNotice && <div className="small mt-2 text-muted">{presetNotice}</div>}
              </div>

              <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
                <div>
                  <div className="fw-semibold">Map import columns</div>
                  <div className="small text-muted">
                    Select the CSV/XLSX header that should populate each database field.
                    <span className="badge bg-info ms-2">{parsed.headers.length} columns detected</span>
                  </div>
                  {mappingSummary.remainingRequired > 0 && (
                    <div className="badge bg-warning text-dark mt-2">
                      {mappingSummary.remainingRequired} required field{mappingSummary.remainingRequired > 1 ? 's' : ''} missing
                    </div>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <div className="btn-group btn-group-sm" role="group" aria-label="Filter mapping">
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('all')}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'missing' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('missing')}
                    >
                      Missing
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'product' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('product')}
                    >
                      Product
                    </button>
                    <button
                      type="button"
                      className={`btn btn-outline-secondary ${mappingFilter === 'document' ? 'active' : ''}`}
                      onClick={() => setMappingFilter('document')}
                    >
                      Document
                    </button>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleAutoMapClick(true)}>
                    Auto map
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setMapping({}); setMappingFilter('all'); }}>
                    Clear selections
                  </button>
                </div>
              </div>

              <div className="row row-cols-1 row-cols-md-2 g-3">
                {filteredFields.map((field) => {
                  const label = display[field.field] || field.field;
                  const isRequired = effectiveRequired.includes(field.field);
                  const missing = !mapping[field.field];
                  const samplePreview = samplePreviewForField(field.field);
                  return (
                    <div className="col" key={field.field}>
                      <div className={`border rounded p-3 h-100 ${missing && isRequired ? 'border-danger' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="fw-semibold">{label} {isRequired && <span className="text-danger">*</span>}</div>
                          <span className="badge bg-light text-muted text-uppercase">
                            {field.field.startsWith('product.')
                              ? 'Product'
                              : field.field.startsWith('alt_part_number.')
                              ? 'Alt Part'
                              : field.field.startsWith('supplier.')
                              ? 'Supplier'
                              : 'Document'}
                          </span>
                        </div>
                        <select
                          className="form-select"
                          value={mapping[field.field] || ''}
                          onChange={(e) => handleMappingChange(field.field, e.target.value)}
                        >
                          <option value="">-- Select column --</option>
                          {(parsed.headers || []).map((header, idx) => (
                            <option key={`${idx}-${header}`} value={header}>{header}</option>
                          ))}
                        </select>
                        <div className="small text-muted mt-2">
                          {field.data_type}{field.max_length ? `, max ${field.max_length}` : ''}
                        </div>
                        <div className="small text-muted mt-1">
                          {mapping[field.field] && (
                            <>
                              <span className="text-uppercase text-secondary">Sample:&nbsp;</span>
                              {samplePreview || 'No sample values'}
                            </>
                          )}
                          {!mapping[field.field] && 'No column selected'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3">
                <button
                  className="btn btn-primary"
                  disabled={startingJob || parsingFile || requiredMissing.length > 0}
                  onClick={startImportJob}
                >
                  {startingJob ? 'Setting up job…' : 'Begin Import'}
                </button>
                {requiredMissing.length > 0 && (
                  <div className="small text-muted mt-2">
                    Map required fields before starting: {requiredMissing.map((field) => display[field] || field).join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {parsed.headers.length > 0 && !parsingFile && (
            <div className="mt-3">
              <div className="fw-semibold">Sample rows ({parsed.sample.length} of {parsed.total_rows})</div>
              <div className="table-responsive" style={{ maxHeight: 240 }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      {parsed.headers.map((h, i) => <th key={i} className="small">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.sample.map((r, i) => (
                      <tr key={i}>
                        {parsed.headers.map((h, j) => <td key={j} className="small">{r[h]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}


        </div>
        <Modal
          title="Apply Saved Mapping?"
          open={confirmPresetModal.open}
          onCancel={closeConfirmPresetModal}
          onOk={confirmApplyDetectedPreset}
          okText="Apply Mapping"
          cancelText="Skip"
        >
          <div className="mb-2">
            Matching preset found: <strong>{confirmPresetModal.preset?.preset_name}</strong>
          </div>
          <div className="small text-muted mb-2">
            Match score: {confirmPresetModal?.matched || 0} / {confirmPresetModal?.required || 0} required mappings matched.
          </div>
          <div className="small text-muted">
            This file appears to match a saved mapping preset. Do you want to apply it now?
          </div>
        </Modal>
        <Modal
          title="Overwrite Mapping Preset?"
          open={confirmOverwriteModal.open}
          onCancel={closeConfirmOverwriteModal}
          onOk={confirmOverwritePreset}
          okText="Overwrite"
          cancelText="Cancel"
        >
          <div>
            A preset named <strong>{confirmOverwriteModal.name}</strong> already exists for this import mode.
          </div>
          <div className="small text-muted mt-2">
            Saving will replace the existing mapping config for that preset name.
          </div>
        </Modal>
      </Card>
      </Space>
    </div>
  );
};

export default DocumentsImportV4;
